import type { TeammateAccountGrading } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { ToolUsageAccuracy } from "@repo/app-types/entities/toolUsageAccuracy";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import type { FC } from "react";
import { useMemo } from "react";
import { GradingsTableContactCell } from "../teammatePerformance/gradingsTableContactCell.js";
import { ToolUsageAccuracyStatusPill } from "../teammatePerformance/toolUsageAccuracyStatusPill.js";

interface GradingWithToolAccuracy extends TeammateAccountGrading {
  toolAccuracy: ToolUsageAccuracy | undefined;
}

export interface ToolUsageAccuracyGradingsTableProps {
  gradings: readonly GradingWithToolAccuracy[];
  unfilteredEntityCount?: number;
  isLoading?: boolean;
  isError?: boolean;
  onGradingAction: (grading: GradingWithToolAccuracy) => void;
  onLoadMore?: () => void;
}

export const ToolUsageAccuracyGradingsTable: FC<
  ToolUsageAccuracyGradingsTableProps
> = (props) => {
  const { gradings } = props;

  const columns = useMemo(
    (): EntityTableColumnDefinition<GradingWithToolAccuracy>[] => [
      {
        id: "customer",
        className: "w-[34%]",
        header: "Customer",
        isRowHeader: true,
        renderCell: (grading) => <GradingsTableContactCell grading={grading} />,
      },
      {
        id: "status",
        className: "w-0",
        header: "Usage",
        renderCell: (grading) => {
          if (!grading.toolAccuracy) {
            return "--";
          }

          return (
            <ToolUsageAccuracyStatusPill status={grading.toolAccuracy.status} />
          );
        },
      },

      {
        id: "summary",
        className: "w-[50%]",
        header: "Commentary",
        renderCell: (grading) => {
          if (!grading.toolAccuracy) {
            return "--";
          }

          return grading.toolAccuracy.summary;
        },
      },
    ],
    [],
  );

  return (
    <EntityTable
      aria-label="Calls with tool accuracy"
      columns={columns}
      entities={gradings}
      unfilteredEntityCount={props.unfilteredEntityCount}
      isLoading={props.isLoading}
      isError={props.isError}
      onLoadMore={props.onLoadMore}
      emptyMessage="No calls with tool usage data."
      onEntityAction={props.onGradingAction}
    />
  );
};
